import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, UserPlus, Users, GraduationCap, CheckCircle2,
  XCircle, Mail, Building, Clock, Eye, Calendar,
  User as UserIcon, BarChart, Target, BookOpen, Award,
  CheckCircle, XCircle as XCircleIcon, ChevronRight, Edit,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import SidebarOverlay from '../../components/common/SidebarOverlay';
import MessageModal from '../../components/common/MessageModal';
import { usersAPI, modulesAPI } from '../../services/api';
import { useRoleData } from '../../hooks/useRoleData';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import { useAppContext } from '../../context/AppContext';
import { formatDate } from '../../utils/dateFormatter';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import './UserEnrollment.css';

const UserEnrollment = () => {
  const [activeTab, setActiveTab] = usePersistentTab('trainees');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Trainee',
    department: '',
    status: 'Active',
    sendWelcomeEmail: true,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [moduleModalOpen, setModuleModalOpen] = useState(false);
  const [userForModule, setUserForModule] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');

  const { showNotification, currentUser } = useAppContext();
  const navigate = useNavigate();
  const { filteredUsers = [], filteredModules = [], allModules = [], refreshData, loading, error } = useRoleData();
  // users from API via useRoleData
  const users = filteredUsers;
  const modules = filteredModules;
  const allModulesList = allModules;

  // Sync selectedUser with fresh data from API when users array updates
  useEffect(() => {
    if (selectedUser) {
      const freshUser = users.find(u => u.id === selectedUser.id);
      if (freshUser) {
        setSelectedUser(freshUser);
      }
    }
  }, [users]);

  const trainees = users.filter((u) => u.role === 'Trainee');
  const instructors = users.filter((u) => u.role === 'Instructor');

  const currentList = activeTab === 'trainees' ? trainees : instructors;

  const filtered = currentList.filter((u) => {
    const matchSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.department.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || u.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const canSendMessage = currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor';

  const canManageUser = (targetUser) => {
    return hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_USERS);
  };

  const canAssignModule = (targetUser) => {
    if (!currentUser) return false;
    if (targetUser.role === 'Instructor') {
      return hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_USERS);
    } else if (targetUser.role === 'Trainee') {
      return hasPermission(currentUser, PERMISSIONS.ENROLL_TRAINEES);
    }
    return false;
  };

  const getModuleNames = (ids) =>
    ids
      ? ids
          .map((id) => allModulesList.find((m) => m.id === id)?.title)
          .filter(Boolean)
          .slice(0, 2)
      : [];

  const normalizeProgress = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.max(0, Math.min(100, Math.round(numericValue)));
  };

  const getEnrolledModuleCount = (user) => {
    if (Array.isArray(user?.enrolledModules)) {
      return user.enrolledModules.length;
    }
    return Number(user?.enrolledModulesCount) || 0;
  };

  const getCompletedModuleCount = (user) => {
    if (Array.isArray(user?.completedModules)) {
      return user.completedModules.length;
    }
    return Number(user?.completedModulesCount) || 0;
  };

  const getTraineeProgress = (user) => {
    if (!user || user.role !== 'Trainee') return 0;

    const reportedProgress = Number(user.progress);

    if (Number.isFinite(reportedProgress)) {
      return normalizeProgress(reportedProgress);
    }

    const totalCount = getEnrolledModuleCount(user);
    const completedCount = getCompletedModuleCount(user);

    if (totalCount > 0) {
      return normalizeProgress((completedCount / totalCount) * 100);
    }

    return 0;
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };


  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim() || !newUser.department.trim()) return;

    try {
      const userData = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        role: newUser.role,
        department: newUser.department,
        status: newUser.status,
        password: 'password123', // default password
        sendWelcomeEmail: newUser.sendWelcomeEmail,
      };

      await usersAPI.createUser(userData);
      showNotification('User created successfully', 'success');
      setNewUser({ firstName: '', lastName: '', email: '', role: 'Trainee', department: '', status: 'Active', sendWelcomeEmail: true });
      setShowAddModal(false);
      refreshData(); // refetch users
    } catch (error) {
      console.error('Failed to create user:', error);
      showNotification(error.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewUser({ firstName: '', lastName: '', email: '', role: 'Trainee', department: '', status: 'Active', sendWelcomeEmail: true });
  };

  const handleViewDetails = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setSelectedUser(freshUser);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setSelectedUser(null);
  };

  const handleOpenEditModal = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setUserToEdit(freshUser);
    const isTrainee = freshUser.role === 'Trainee';

    const formData = {
      ...freshUser,
      // Copy role-appropriate arrays
      ...(isTrainee ? {
        enrolledModules: freshUser.enrolledModules ? [...freshUser.enrolledModules] : [],
        completedModules: freshUser.completedModules ? [...freshUser.completedModules] : [],
        progress: freshUser.progress || 0,
      } : {
        assignedModules: freshUser.assignedModules ? [...freshUser.assignedModules] : [],
        specialization: freshUser.specialization || '',
      })
    };

    setEditForm(formData);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setUserToEdit(null);
    setEditForm(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type } = e.target;

    // Convert numeric fields
    let processedValue = value;
    if (name === 'progress') {
      const num = parseInt(value, 10);
      processedValue = isNaN(num) ? 0 : Math.min(100, Math.max(0, num));
    }

    setEditForm(prev => {
      const updated = { ...prev, [name]: processedValue };

      // Handle role change
      if (name === 'role' && prev.role !== processedValue) {
        // Clear role-specific fields when role changes
        if (processedValue === 'Trainee') {
          // Switching to Trainee: clear instructor fields
          delete updated.assignedModules;
          delete updated.specialization;
          // Initialize trainee fields if not present
          if (!updated.enrolledModules) updated.enrolledModules = [];
          if (!updated.completedModules) updated.completedModules = [];
          if (!updated.progress) updated.progress = 0;
        } else if (processedValue === 'Instructor') {
          // Switching to Instructor: clear trainee fields
          delete updated.enrolledModules;
          delete updated.completedModules;
          delete updated.progress;
          // Initialize instructor fields if not present
          if (!updated.assignedModules) updated.assignedModules = [];
          if (!updated.specialization) updated.specialization = '';
        }
      }

      return updated;
    });
  };

  const handleModuleToggle = (moduleId) => {
    if (!editForm) return;

    const isTrainee = editForm.role === 'Trainee';
    const arrayField = isTrainee ? 'enrolledModules' : 'assignedModules';
    const currentArray = editForm[arrayField] || [];

    const isAdding = !currentArray.includes(moduleId);
    const newArray = isAdding
      ? [...currentArray, moduleId]
      : currentArray.filter(id => id !== moduleId);

    // For Trainees: if removing from enrolled modules, also remove from completed modules
    let updates = { [arrayField]: newArray };
    if (isTrainee && !isAdding) {
      const currentCompleted = editForm.completedModules || [];
      updates.completedModules = currentCompleted.filter(id => id !== moduleId);
    }

    setEditForm(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleCompletedModuleToggle = (moduleId) => {
    if (!editForm || editForm.role !== 'Trainee') return;

    const currentArray = editForm.completedModules || [];
    const newArray = currentArray.includes(moduleId)
      ? currentArray.filter(id => id !== moduleId)
      : [...currentArray, moduleId];

    setEditForm(prev => ({
      ...prev,
      completedModules: newArray
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editForm?.firstName?.trim() || !editForm?.lastName?.trim() || !editForm?.email?.trim() || !editForm?.department?.trim()) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    // Clean the data before saving - remove role-inappropriate fields
    const isTrainee = editForm.role === 'Trainee';
    const cleanedData = { ...editForm };

    // Remove fields that don't belong to the current role
    if (isTrainee) {
      delete cleanedData.assignedModules;
      delete cleanedData.specialization;
      // Ensure trainee fields exist
      if (!cleanedData.enrolledModules) cleanedData.enrolledModules = [];
      if (!cleanedData.completedModules) cleanedData.completedModules = [];
      if (cleanedData.progress === undefined) cleanedData.progress = 0;
    } else {
      delete cleanedData.enrolledModules;
      delete cleanedData.completedModules;
      delete cleanedData.progress;
      // Ensure instructor fields exist
      if (!cleanedData.assignedModules) cleanedData.assignedModules = [];
      if (!cleanedData.specialization) cleanedData.specialization = '';
    }

    try {
      await usersAPI.updateUser(userToEdit.id, cleanedData);

      // Update selectedUser if it's the same user
      if (selectedUser && selectedUser.id === userToEdit.id) {
        setSelectedUser(prev => ({
          ...prev,
          ...cleanedData
        }));
      }

      showNotification('Profile updated successfully', 'success');
      setEditModalOpen(false);
      setUserToEdit(null);
      setEditForm(null);
      refreshData(); // refetch users
    } catch (error) {
      console.error('Failed to update user:', error);
      showNotification(error.response?.data?.error || 'Failed to update user', 'error');
    }
  };

  const handleOpenModuleModal = (user) => {
    // Get fresh user data from the current users array to ensure we have the latest data
    const freshUser = users.find(u => u.id === user.id) || user;
    setUserForModule(freshUser);
    // Determine modules user is already enrolled/assigned to
    const currentModules = freshUser.role === 'Trainee'
      ? (freshUser.enrolledModules || [])
      : (freshUser.assignedModules || []);
    // Filter modules that are NOT already assigned/enrolled
    const available = modules.filter(m => !currentModules.includes(m.id));
    setSelectedModuleIds([]); // start with empty selection
    setModuleModalOpen(true);
  };

  const handleCloseModuleModal = () => {
    setModuleModalOpen(false);
    setUserForModule(null);
    setSelectedModuleIds([]);
  };

  const handleModuleSelection = (moduleId) => {
    setSelectedModuleIds(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleSaveModuleAssignment = async () => {
    if (!userForModule || selectedModuleIds.length === 0) {
      showNotification('Please select at least one module', 'error');
      return;
    }

    try {
      const isTrainee = userForModule.role === 'Trainee';

      // For each selected module, call appropriate API
      const promises = selectedModuleIds.map(moduleId => {
        if (isTrainee) {
          return usersAPI.enrollTrainee(userForModule.id, moduleId);
        } else {
          return modulesAPI.assignInstructor(moduleId, userForModule.id);
        }
      });

      await Promise.all(promises);

      // Update selectedUser if it's the same user
      if (selectedUser && selectedUser.id === userForModule.id) {
        const isTrainee = selectedUser.role === 'Trainee';
        const field = isTrainee ? 'enrolledModules' : 'assignedModules';
        const current = selectedUser[field] || [];
        const updated = [...current, ...selectedModuleIds.filter(id => !current.includes(id))];
        setSelectedUser({
          ...selectedUser,
          [field]: updated
        });
      }

      showNotification(
        `${userForModule.role === 'Trainee' ? 'Enrolled' : 'Assigned'} ${selectedModuleIds.length} module(s) successfully`,
        'success'
      );
      handleCloseModuleModal();
      refreshData(); // refetch users and modules
    } catch (error) {
      console.error('Failed to assign modules:', error);
      showNotification(error.response?.data?.error || 'Failed to assign modules', 'error');
    }
  };

  const handleOpenMessageModal = (email) => {
    setSelectedEmail(email);
    setMessageModalOpen(true);
  };

  const handleCloseMessageModal = () => {
    setMessageModalOpen(false);
    setSelectedEmail('');
  };

  const handleSendMessage = (messageData) => {
    // Simulate sending message
    showNotification(`Message sent to ${messageData.recipientEmail}`, 'success');
  };

  const renderUserDetails = () => {
    if (!selectedUser) return null;
    const user = selectedUser;
    const isTrainee = user.role === 'Trainee';
    const moduleIds = isTrainee ? user.enrolledModules : user.assignedModules;
    const moduleNames = getModuleNames(moduleIds);
    const completedCount = isTrainee ? getCompletedModuleCount(user) : null;
    const totalCount = isTrainee ? getEnrolledModuleCount(user) : (user.assignedModules?.length || 0);
    const overallProgress = isTrainee ? getTraineeProgress(user) : 0;

    return (
      <div className="user-details">
        {/* Header with avatar and basic info */}
        <div className="user-details__header">
          <div
            className="avatar user-details__avatar"
            style={{
              width: 72,
              height: 72,
              fontSize: '1.5rem',
              background:
                user.role === 'Instructor'
                  ? 'linear-gradient(135deg, var(--color-blue), #1e40af)'
                  : 'linear-gradient(135deg, var(--color-lime), var(--color-medium-green))',
            }}
          >
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              user.initials
            )}
          </div>
          <div className="user-details__header-info">
            <h2 className="user-details__name">{user.name}</h2>
            <div className="user-details__meta">
              {user.username && (
                <span className="user-details__username" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>@{user.username}</span>
              )}
              <span className={`badge ${user.role === 'Instructor' ? 'badge-blue' : 'badge-green'}`}>
                {user.role}
              </span>
              <span className={`badge ${user.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {user.status}
              </span>
              <span className="user-details__token">ID: {user.id}</span>
            </div>
            <p className="user-details__department">{user.department}</p>
          </div>
        </div>

        {/* Details grid */}
        <div className="user-details__grid">
          <div className="user-details__item">
            <Mail size={14} className="user-details__icon" />
            <div>
              <span className="user-details__label">Email</span>
              {canSendMessage ? (
                <button
                  className="btn-link user-details__value"
                  onClick={() => handleOpenMessageModal(user.email)}
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                >
                  {user.email}
                </button>
              ) : (
                <span className="user-details__value">{user.email}</span>
              )}
            </div>
          </div>
          <div className="user-details__item">
            <Calendar size={14} className="user-details__icon" />
            <div>
              <span className="user-details__label">Joined</span>
              <span className="user-details__value">{formatDate(user.joinDate)}</span>
            </div>
          </div>
          <div className="user-details__item">
            <Clock size={14} className="user-details__icon" />
            <div>
              <span className="user-details__label">Last Active</span>
              <span className="user-details__value">{formatDate(user.lastActive)}</span>
            </div>
          </div>
          {currentUser?.role !== 'Administrator' && currentUser?.role !== 'Instructor' && (
            <div className="user-details__item">
              <Target size={14} className="user-details__icon" />
              <div>
                <span className="user-details__label">Access Token</span>
                <span className="user-details__value user-details__token-code">{user.token}</span>
              </div>
            </div>
          )}
          {user.role === 'Instructor' && user.specialization && (
            <div className="user-details__item">
              <Award size={14} className="user-details__icon" />
              <div>
                <span className="user-details__label">Specialization</span>
                <span className="user-details__value">{user.specialization}</span>
              </div>
            </div>
          )}
        </div>

        {/* Progress for trainees */}
        {isTrainee && user.progress !== undefined && (
          <div className="user-details__section">
            <h3 className="user-details__section-title">
              <BarChart size={16} />
              Overall Progress
            </h3>
            <div className="user-details__progress">
              <div className="user-details__progress-header">
                <span>Average Module Progress</span>
                <span className="user-details__progress-pct">{overallProgress}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} />
              </div>
              <div className="user-details__progress-stats">
                <span>Completed: {completedCount} of {totalCount} modules</span>
              </div>
            </div>
          </div>
        )}

        {/* Modules */}
        <div className="user-details__section">
          <h3 className="user-details__section-title">
            <BookOpen size={16} />
            {isTrainee ? 'Enrolled Modules' : 'Assigned Modules'}
          </h3>
          {moduleIds && moduleIds.length > 0 ? (
            <div className="user-details__modules">
              {moduleNames.map((name, idx) => (
                <div key={idx} className="user-details__module">
                  <ChevronRight size={12} className="user-details__module-icon" />
                  <span className="user-details__module-name">{name}</span>
                  {isTrainee && user.completedModules?.includes(moduleIds[idx]) && (
                    <span className="badge badge-green badge-sm">Completed</span>
                  )}
                </div>
              ))}
              {moduleIds.length > moduleNames.length && (
                <div className="user-details__module-more">
                  +{moduleIds.length - moduleNames.length} more modules
                </div>
              )}
            </div>
          ) : (
            <p className="user-details__empty">No modules assigned yet.</p>
          )}
        </div>

        {/* Actions */}
        <div className="user-details__actions">
          {user.role === 'Trainee' && user.username && (
            <button className="btn btn-primary btn-sm" onClick={() => { handleCloseSidebar(); navigate(`/trainees/${user.username}`); }}>
              <UserIcon size={14} />
              View Full Profile
            </button>
          )}
          {canManageUser(user) && (
            <button className="btn btn-outline btn-sm">
              {user.status === 'Active' ? 'Deactivate' : 'Activate'}
            </button>
          )}
          {canAssignModule(user) && (
            <button className="btn btn-primary btn-sm" onClick={() => handleOpenModuleModal(user)}>
              {isTrainee ? 'Enroll Module' : 'Assign Module'}
            </button>
          )}
          {canManageUser(user) && (
            <button className="btn btn-ghost btn-sm" onClick={() => handleOpenEditModal(user)}>
              <Edit size={14} />
              Edit Profile
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="enrollment">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>User Enrollment</h1>
          <p>Manage trainees and instructors across all modules</p>
        </div>
        {canManageUser(null) && (
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <UserPlus size={15} />
            Add User
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="enrollment__summary">
        {[
          { icon: Users, label: 'Total Trainees', value: trainees.length, color: 'green' },
          { icon: GraduationCap, label: 'Instructors', value: instructors.length, color: 'blue' },
          { icon: CheckCircle2, label: 'Active Users', value: users.filter((u) => u.status === 'Active').length, color: 'dark' },
          { icon: Clock, label: 'Inactive Users', value: users.filter((u) => u.status === 'Inactive').length, color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`enrollment__summary-card enrollment__summary-card--${color}`}>
            <div className="enrollment__summary-icon">
              <Icon size={20} />
            </div>
            <div>
              <span className="enrollment__summary-value">{value}</span>
              <span className="enrollment__summary-label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="enrollment__tab-row">
        <div className="enrollment__tabs">
          <button
            className={`enrollment__tab ${activeTab === 'trainees' ? 'active' : ''}`}
            onClick={() => setActiveTab('trainees')}
          >
            <Users size={15} />
            Trainees
            <span className="enrollment__tab-count">{trainees.length}</span>
          </button>
          <button
            className={`enrollment__tab ${activeTab === 'instructors' ? 'active' : ''}`}
            onClick={() => setActiveTab('instructors')}
          >
            <GraduationCap size={15} />
            Instructors
            <span className="enrollment__tab-count">{instructors.length}</span>
          </button>
        </div>

        <div className="enrollment__filters">
          <div className="content-lib__search">
            <Search size={15} className="content-lib__search-icon" />
            <input
              className="content-lib__search-input"
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
          <select
            className="content-lib__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* User Cards */}
      {loading ? (
        <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
          <div className="empty-state-icon"><Clock size={28} /></div>
          <h3>Loading users...</h3>
        </div>
      ) : error ? (
        <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
          <div className="empty-state-icon"><XCircle size={28} /></div>
          <h3>Error loading users</h3>
          <p>{error}</p>
          <button className="btn btn-outline btn-sm" onClick={refreshData}>Retry</button>
        </div>
      ) : (
        <div className="enrollment__grid">
          {filtered.length === 0 ? (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              <div className="empty-state-icon"><Users size={28} /></div>
              <h3>No users found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            filtered.map((u) => (
              <div key={u.id} className="enrollment__card card">
                <div className="enrollment__card-header">
                  <div
                    className="avatar"
                    style={{
                      width: 52,
                      height: 52,
                      fontSize: '1rem',
                      background:
                        u.role === 'Instructor'
                          ? 'linear-gradient(135deg, var(--color-blue), #1e40af)'
                          : 'linear-gradient(135deg, var(--color-lime), var(--color-medium-green))',
                    }}
                  >
                    {u.avatar ? (
                      <img src={u.avatar} alt={u.name} />
                    ) : (
                      u.initials
                    )}
                  </div>
                  <div className="enrollment__card-user">
                    <span className="enrollment__card-name">{u.name}</span>
                    <span className="enrollment__card-role">
                      {u.role === 'Instructor' ? (
                        <span className="badge badge-blue">{u.role}</span>
                      ) : (
                        <span className="badge badge-green">{u.role}</span>
                      )}
                    </span>
                  </div>
                  <div className="enrollment__card-status">
                    {u.status === 'Active' ? (
                      <CheckCircle2 size={18} style={{ color: 'var(--color-lime)' }} />
                    ) : (
                      <XCircle size={18} style={{ color: '#d97706' }} />
                    )}
                  </div>
                </div>

                <div className="enrollment__card-info">
                  <div className="enrollment__info-row">
                    <Mail size={13} />
                    {canSendMessage ? (
                      <button
                        className="btn-link"
                        onClick={() => handleOpenMessageModal(u.email)}
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                      >
                        {u.email}
                      </button>
                    ) : (
                      <span>{u.email}</span>
                    )}
                  </div>
                  <div className="enrollment__info-row">
                    <Building size={13} />
                    <span>{u.department}</span>
                  </div>
                  <div className="enrollment__info-row">
                    <Clock size={13} />
                    <span>Last active: {formatDate(u.lastActive)}</span>
                  </div>
                </div>

                {/* Modules */}
                <div className="enrollment__card-modules">
                  <span className="enrollment__modules-label">
                    {u.role === 'Instructor' ? 'Assigned Modules' : 'Enrolled Modules'}
                  </span>
                  <div className="enrollment__modules-list">
                    {(u.role === 'Instructor' ? getModuleNames(u.assignedModules) : getModuleNames(u.enrolledModules)).map((name) => (
                      <span key={name} className="enrollment__module-tag">{name}</span>
                    ))}
                    {((u.role === 'Instructor' ? u.assignedModules : u.enrolledModules) || []).length > 2 && (
                      <span className="enrollment__module-more">
                        +{((u.role === 'Instructor' ? u.assignedModules : u.enrolledModules) || []).length - 2} more
                      </span>
                    )}
                    {((u.role === 'Instructor' ? u.assignedModules : u.enrolledModules) || []).length === 0 && (
                      <span className="enrollment__module-none">None assigned</span>
                    )}
                  </div>
                </div>

                {/* Progress (trainees only) */}
                {u.role === 'Trainee' && u.progress !== undefined && (
                  <div className="enrollment__card-progress">
                    <div className="enrollment__progress-header">
                      <span>Overall Progress</span>
                      <span className="enrollment__progress-pct">{getTraineeProgress(u)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${getTraineeProgress(u)}%` }} />
                    </div>
                  </div>
                )}

                <div className="enrollment__card-actions">
                  {canManageUser(u) && (
                    <button className="btn btn-outline btn-sm">
                      {u.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                  {canAssignModule(u) && (
                    <button className="btn btn-primary btn-sm" onClick={() => handleOpenModuleModal(u)}>
                      {u.role === 'Instructor' ? 'Assign Module' : 'Enroll Module'}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => handleViewDetails(u)}>
                    <Eye size={14} />
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        title="Add New User"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleCloseModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleAddUser}>
              Add User
            </button>
          </div>
        }
      >
        <form onSubmit={handleAddUser}>
          <div className="form-group">
            <label className="form-label">First Name *</label>
            <input
              className="form-input"
              type="text"
              name="firstName"
              value={newUser.firstName}
              onChange={handleNewUserChange}
              placeholder="Enter first name"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Last Name *</label>
            <input
              className="form-input"
              type="text"
              name="lastName"
              value={newUser.lastName}
              onChange={handleNewUserChange}
              placeholder="Enter last name"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              className="form-input"
              type="email"
              name="email"
              value={newUser.email}
              onChange={handleNewUserChange}
              placeholder="user@nlng.com"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select
              className="form-select"
              name="role"
              value={newUser.role}
              onChange={handleNewUserChange}
              required
            >
              <option value="Trainee">Trainee</option>
              <option value="Instructor">Instructor</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Department *</label>
            <input
              className="form-input"
              type="text"
              name="department"
              value={newUser.department}
              onChange={handleNewUserChange}
              placeholder="e.g., HSE Unit"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Status *</label>
            <select
              className="form-select"
              name="status"
              value={newUser.status}
              onChange={handleNewUserChange}
              required
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={newUser.sendWelcomeEmail}
                onChange={(e) => setNewUser({ ...newUser, sendWelcomeEmail: e.target.checked })}
              />
              <span>Send welcome email with sign-in details</span>
            </label>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title={`Edit ${userToEdit?.role || 'User'} Profile`}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleCloseEditModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit}>
              Save Changes
            </button>
          </div>
        }
      >
        {editForm && (
          <form onSubmit={handleSaveEdit}>
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                className="form-input"
                type="text"
                name="firstName"
                value={editForm.firstName || ''}
                onChange={handleEditFormChange}
                placeholder="Enter first name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                className="form-input"
                type="text"
                name="lastName"
                value={editForm.lastName || ''}
                onChange={handleEditFormChange}
                placeholder="Enter last name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                className="form-input"
                type="email"
                name="email"
                value={editForm.email || ''}
                onChange={handleEditFormChange}
                placeholder="user@nlng.com"
                required
                readOnly={currentUser.role !== 'Administrator'}
                disabled={currentUser.role !== 'Administrator'}
                style={currentUser.role !== 'Administrator' ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
              {currentUser.role !== 'Administrator' && (
                <p className="text-xs text-muted" style={{ marginTop: '4px' }}>
                  Only administrators can change email addresses
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select
                className="form-select"
                name="role"
                value={editForm.role || 'Trainee'}
                onChange={handleEditFormChange}
                required
              >
                <option value="Trainee">Trainee</option>
                <option value="Instructor">Instructor</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Department *</label>
              <input
                className="form-input"
                type="text"
                name="department"
                value={editForm.department || ''}
                onChange={handleEditFormChange}
                placeholder="e.g., HSE Unit"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                className="form-select"
                name="status"
                value={editForm.status || 'Active'}
                onChange={handleEditFormChange}
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Role-specific fields will be added here */}
            {editForm.role === 'Trainee' && (
              <>
                <div className="form-group">
                  <label className="form-label">Progress (%) (auto-calculated)</label>
                  <input
                    className="form-input"
                    type="number"
                    name="progress"
                    min="0"
                    max="100"
                    value={editForm.progress || 0}
                    onChange={handleEditFormChange}
                    placeholder="0-100"
                    disabled
                    style={{ opacity: 0.7, cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Specialization (for Instructor)</label>
                  <input
                    className="form-input"
                    type="text"
                    name="specialization"
                    value={editForm.specialization || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g., Safety Procedures"
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                  <small className="text-muted">This field is only for Instructors</small>
                </div>
              </>
            )}

            {editForm.role === 'Instructor' && (
              <>
                <div className="form-group">
                  <label className="form-label">Specialization</label>
                  <input
                    className="form-input"
                    type="text"
                    name="specialization"
                    value={editForm.specialization || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g., Safety Procedures"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Progress (%) (for Trainee)</label>
                  <input
                    className="form-input"
                    type="number"
                    name="progress"
                    min="0"
                    max="100"
                    value={editForm.progress || 0}
                    onChange={handleEditFormChange}
                    placeholder="0-100"
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  />
                  <small className="text-muted">This field is only for Trainees</small>
                </div>
              </>
            )}



          </form>
        )}
      </Modal>

      {/* Module Assignment Modal */}
      <Modal
        isOpen={moduleModalOpen}
        onClose={handleCloseModuleModal}
        title={`${userForModule?.role === 'Trainee' ? 'Enroll' : 'Assign'} Module${selectedModuleIds.length > 1 ? 's' : ''}`}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={handleCloseModuleModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveModuleAssignment}>
              {userForModule?.role === 'Trainee' ? 'Enroll' : 'Assign'} Selected
            </button>
          </div>
        }
      >
        {userForModule && (
          <div>
            <p className="text-muted" style={{ marginBottom: 'var(--space-4)' }}>
              Select module(s) to {userForModule.role === 'Trainee' ? 'enroll' : 'assign'} <strong>{userForModule.name}</strong> in.
            </p>
            <div className="modules-checkbox-group">
              {modules.map(module => {
                const isAlready = userForModule.role === 'Trainee'
                  ? (userForModule.enrolledModules || []).includes(module.id)
                  : (userForModule.assignedModules || []).includes(module.id);
                if (isAlready) return null; // Skip already enrolled/assigned modules
                const isChecked = selectedModuleIds.includes(module.id);
                return (
                  <div key={module.id} className="module-checkbox">
                    <input
                      type="checkbox"
                      id={`module-assign-${module.id}`}
                      checked={isChecked}
                      onChange={() => handleModuleSelection(module.id)}
                    />
                    <label htmlFor={`module-assign-${module.id}`} className="module-checkbox-label">
                      {module.title}
                      <span className="module-checkbox-category">({module.category})</span>
                    </label>
                  </div>
                );
              })}
            </div>
            {selectedModuleIds.length === 0 && (
              <p className="text-muted" style={{ marginTop: 'var(--space-4)', fontStyle: 'italic' }}>
                No modules selected. Please select at least one module.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Message Modal */}
      <MessageModal
        isOpen={messageModalOpen}
        onClose={handleCloseMessageModal}
        recipientEmail={selectedEmail}
        currentUserRole={currentUser?.role}
        onSend={handleSendMessage}
      />

      {/* User Details Sidebar */}
      <SidebarOverlay
        isOpen={sidebarOpen}
        onClose={handleCloseSidebar}
        title={`${selectedUser?.role} Details`}
        width="520px"
      >
        {renderUserDetails()}
      </SidebarOverlay>
    </div>
  );
};

export default UserEnrollment;
